// React.lazy that survives a deploy.
//
// Every map on this site is behind a `lazy(() => import(...))` so maplibre-gl
// (~250 KB gz) stays out of the entry bundle. That split has a failure mode the
// eager imports don't have: the chunk is fetched by name, minutes or days after
// the bundle that names it was loaded, and an Amplify Hosting deploy REPLACES
// the whole site -- so the old hashed chunk is deleted the moment a new build
// lands. A tab still running yesterday's bundle asks for a file that is gone
// and gets a 404.
//
// React.lazy has no recovery for that: the rejected import surfaces as a render
// error, and the only error boundary in the app is the one wrapping everything
// in App.jsx -- so one missing 71 KB chunk replaced the entire page with
// "Something went wrong on our end." That is what happened to
// /me/item/.../trips/group on 2026-08-31 (GET /static/js/423.60bbf1e4.chunk.js
// -> 404, the build before it having renamed 423 to a56aa716).
//
// The recovery is a reload: the new index.html names the chunks that exist.
// Three things make that safe rather than a way to build a reload loop.
//
// - It only fires for a CHUNK LOAD failure, never for a module that threw while
//   evaluating. Reloading fixes the first; the second is a bug in our own code
//   and would be masked by a page that silently reloaded itself.
// - It fires at most ONCE per component per tab, recorded in sessionStorage.
//   A second failure means the reload didn't help (a genuinely broken deploy,
//   or a browser that won't let go of its cached document) and falls through to
//   the error screen -- today's behaviour, which is the right floor. So does a
//   reload that never arrives, after a grace period (see recoverByReloading):
//   the error screen is the floor here too, and a spinner that never resolves
//   is below it.
// - If sessionStorage can't be read, nothing reloads. Without the flag there is
//   nothing bounding the loop, and an error screen is much cheaper than a tab
//   that reloads forever.
//
// Note that the reload goes through beforeunload, so `usePendingSavesUnloadWarning`
// still gets to warn about writes that haven't landed. That's deliberate: a
// chunk 404 is not a reason to discard someone's unsaved edit without asking.

import { lazy } from 'react';

const FLAG_PREFIX = 'tp.chunkReload.';

// Webpack 5 (what react-scripts builds with) throws a ChunkLoadError for a JS
// chunk and sets code CSS_CHUNK_LOAD_FAILED for a stylesheet one. The message
// patterns cover native dynamic import, whose wording differs per engine --
// Safari says "Importing a module script failed", Chrome and Firefox say some
// variant of "Failed to fetch dynamically imported module".
//
// Anything unrecognized is deliberately NOT reloadable: see the header. It is
// logged so a spelling we haven't seen is diagnosable from a user's console
// rather than invisible.
const CHUNK_ERROR_MESSAGES =
  /loading (css )?chunk .* failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  if (err.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return CHUNK_ERROR_MESSAGES.test(String(err.message ?? err));
}

// sessionStorage rather than localStorage: the flag is about THIS tab's stale
// bundle, and a tab opened tomorrow should get its own reload if it needs one.
// Every access is guarded -- a browser that refuses storage reports "already
// reloaded", which turns the recovery off rather than making it unbounded.
function alreadyReloaded(name) {
  try {
    return window.sessionStorage.getItem(FLAG_PREFIX + name) !== null;
  } catch {
    return true;
  }
}

function markReloaded(name) {
  try {
    window.sessionStorage.setItem(FLAG_PREFIX + name, '1');
    return true;
  } catch {
    return false;
  }
}

function clearReloaded(name) {
  try {
    window.sessionStorage.removeItem(FLAG_PREFIX + name);
  } catch {
    /* nothing to clear if we can't reach storage */
  }
}

// How long the cache-refill fetch gets before we reload without it. It is an
// optimization, not the fix, and an unbounded fetch would hold the whole
// recovery open behind a request nobody is waiting on.
const REFILL_TIMEOUT_MS = 3000;

// How long to hold the Suspense fallback after asking for a reload. See
// recoverByReloading -- this bounds the wait on a reload that may never come.
const RELOAD_GRACE_MS = 10000;

// The recovery step, behind an object so a test can replace it: jsdom's
// window.location is not reliably writable, and the alternative is every test
// of this module fighting the environment instead of the logic.
export const chunkReloader = {
  reload: async () => {
    // A plain reload trusts the browser's HTTP cache for the document, and that
    // cache is exactly what put us here: on 2026-08-31 Safari served both the
    // document and main.js from its own cache with no conditional request, even
    // though Amplify serves them `max-age=0`. Refilling the entry from the
    // network first is what makes the reload pick up the new chunk names.
    const abort = new AbortController();
    const cutOff = setTimeout(() => abort.abort(), REFILL_TIMEOUT_MS);
    try {
      await fetch(window.location.href, {
        cache: 'reload',
        credentials: 'same-origin',
        signal: abort.signal,
      });
    } catch {
      /* best effort -- reload anyway, the plain one is still usually enough */
    } finally {
      clearTimeout(cutOff);
    }
    window.location.reload();
  },
};

// At most one reload per page life. Two components failing together is not a
// reason to fetch and reload twice, and the per-component sessionStorage flags
// are what keep each one's protection separate across the reload. Module state
// rather than a flag: a reload that lands resets it by destroying the page, so
// the only thing this can suppress is a second attempt within one page life.
let reloadTriggered = false;

// Ask for a reload, then hold the Suspense fallback -- but only for as long as
// a reload could plausibly still be coming.
//
// Settling immediately would paint either a half-built page or the error screen
// for the frame before the reload lands, which is why this waits at all. But
// waiting forever is its own bug: the reload goes through beforeunload, so
// `usePendingSavesUnloadWarning` can put a "Leave site?" dialog in front of it,
// and a user with unsaved edits who picks Stay cancels the reload outright.
// React.lazy caches this promise, so a never-settling one would outlive the
// page -- every later render of this component in this tab would suspend on the
// same dead promise, showing a spinner with no explanation and no way back
// short of a manual refresh. Failing to the error screen at least says that
// something happened.
async function recoverByReloading(err) {
  if (!reloadTriggered) {
    reloadTriggered = true;
    chunkReloader.reload();
  }
  await new Promise((resolve) => { setTimeout(resolve, RELOAD_GRACE_MS); });
  throw err;
}

// One attempt at the import, then a second, then the reload.
//
// The retry is immediate, and what it buys is a request that was aborted in
// flight -- a tab waking from sleep, a network handover -- which webpack really
// does re-issue, because it drops a failed chunk from its installed-chunks map.
// It will NOT outlast a network that is still down, and for the stale-bundle
// case it is one wasted request before the reload. Both are the right trade for
// a case it can actually fix at no added latency.
async function loadChunk(factory, name) {
  try {
    const loaded = await factory();
    // A success means this tab is on a bundle whose chunks exist. Drop the
    // flag so a LATER deploy, in this same long-lived tab, gets its own reload.
    clearReloaded(name);
    return loaded;
  } catch (first) {
    if (!isChunkLoadError(first)) throw first;
    try {
      const loaded = await factory();
      clearReloaded(name);
      return loaded;
    } catch (second) {
      if (!isChunkLoadError(second)) throw second;
      if (alreadyReloaded(name) || !markReloaded(name)) throw second;
      return recoverByReloading(second);
    }
  }
}

// Drop-in for React.lazy. `name` identifies the COMPONENT, not the chunk -- it
// keys the once-per-tab flag, so it has to stay the same across the deploy the
// flag is protecting against. Use the component's name.
export function lazyWithReload(factory, name) {
  if (!name) throw new Error('lazyWithReload needs a stable name');
  return lazy(() => loadChunk(factory, name));
}

// Exported for tests only. `resetForTests` clears the once-per-page-life reload
// guard, which a real page only ever clears by being replaced.
export const _internal = {
  isChunkLoadError,
  loadChunk,
  FLAG_PREFIX,
  RELOAD_GRACE_MS,
  resetForTests: () => { reloadTriggered = false; },
};
